/**
 * Überleitung der Anfo-Kachel (GF-Rückfrage 27.10.2026: „38 Anfos, aber 13,4 % = 33 von 246 Teilnehmern — wo sind die
 * anderen 5?"). Zerlegt die Kachelzahl in vier disjunkte Teile, die exakt zu `orders` summieren:
 * von Teilnehmern · angemeldet, nicht live · ohne Anmeldung · vorläufig. Vorläufig ergänzte Anfos stehen nur im
 * letzten Teil (sie zählen in keiner Quote, auch nicht bei den Teilnehmern).
 * Mit Tracking-Warnung ist „live dabei" nicht messbar (`attendee_orders` ist dann 0) — dann ein gemeinsamer Teil
 * „mit Anmeldung (Teilnahme nicht messbar)" statt einer falschen Aufteilung. Dasselbe, wenn die Show-up-Kachel „—"
 * zeigt (`teilnahmeUnmessbar`: Messlücke, übernommene Anmeldungen, Tracking-Warnung laut `d.show_up.grund`) —
 * P5-Review #3: sonst zeigte die Anfo-Kachel eine Teilnehmer-Aufteilung, die die Show-up-Kachel daneben verneint.
 */

export interface UeberleitungEingabe {
  orders: number;
  attendee_orders: number;
  orders_provisional?: number;
  /** Mit Anmeldung, ohne vorläufige — fehlt bei älterem Backend, dann keine Überleitung. */
  orders_with_registration_fest?: number;
  tracking_suspect?: boolean;
  /** Teilnahme nicht messbar (Show-up-Kachel zeigt „—" mit Grund) — wie `tracking_suspect`. */
  teilnahmeUnmessbar?: boolean;
}

export interface UeberleitungTeil {
  key: 'teilnehmer' | 'nicht_live' | 'anmeldung_unmessbar' | 'ohne_anmeldung' | 'vorlaeufig';
  label: string;
  wert: number;
}

/** `null`, wenn die Teile nicht exakt aufgehen — dann zeigt die Kachel lieber nichts als eine falsche Rechnung. */
export function anfoUeberleitung(e: UeberleitungEingabe): UeberleitungTeil[] | null {
  if (e.orders_with_registration_fest === undefined) return null;
  const vorlaeufig = e.orders_provisional ?? 0;
  const mitAnmeldung = e.orders_with_registration_fest;
  const ohneAnmeldung = e.orders - vorlaeufig - mitAnmeldung;
  const teile: UeberleitungTeil[] = [];
  if (e.tracking_suspect || e.teilnahmeUnmessbar) {
    teile.push({ key: 'anmeldung_unmessbar', label: 'mit Anmeldung (Teilnahme nicht messbar)', wert: mitAnmeldung });
  } else {
    teile.push({ key: 'teilnehmer', label: 'von Teilnehmern', wert: e.attendee_orders });
    teile.push({ key: 'nicht_live', label: 'angemeldet, nicht live', wert: mitAnmeldung - e.attendee_orders });
  }
  teile.push({ key: 'ohne_anmeldung', label: 'ohne Anmeldung', wert: ohneAnmeldung });
  teile.push({ key: 'vorlaeufig', label: 'vorläufig', wert: vorlaeufig });
  if (teile.some((t) => t.wert < 0)) return null;
  if (teile.reduce((s, t) => s + t.wert, 0) !== e.orders) return null;
  // Teilnehmer bleiben auch bei 0 stehen (die Zahl ist die Aussage), die übrigen Teile nur, wenn es sie gibt.
  return teile.filter((t) => t.wert > 0 || t.key === 'teilnehmer' || t.key === 'anmeldung_unmessbar');
}
